import React, { ReactElement, useState, useRef } from 'react';
import { find, forEach, keys } from 'lodash';
import TranslateManager from 'ts-translate-js';
import { ModalPopup } from '../modal';
import { MembershipCardPickupControl } from '../../organisms/memberships/membershipcardpickup';
import type { UUID } from '../../../types/basetypes';
import type { MembershipTypeSaleInfoModel } from '../../../tsrestapi/models';
import type{ PriceChangeSuggestion, UserMembershipCard } from '../../../mvcapi/models/buyticket/user-membership-card';

import './membership-card-pub.scss';
import { MembershipCardPub } from './membership-card-pub';
import { AlreadyMember } from '../../organisms/memberships';
import { useIFrameResize } from '../../../logic/hooks/utils';

const tr = TranslateManager.boundTR;

interface MembershipCardPubsProps {
    membershipTypeSaleInfos: MembershipTypeSaleInfoModel[];
    activeMembershipCards?: UserMembershipCard[];
    onMembershipTypeClick?: (id: UUID) => void;
    refreshActiveMembershipCards: (membershipTypeId: string, status: number, priceChangeSuggestions: PriceChangeSuggestion[]) => void; // status is MembershipCardStatusEnum
    showBecomeMemberButtons?: boolean;
    showMoreInfosLink?: boolean;
}

// INFO: New sale

export function MembershipCardPubs({membershipTypeSaleInfos, activeMembershipCards = [], refreshActiveMembershipCards, showBecomeMemberButtons = true, showMoreInfosLink = false}: MembershipCardPubsProps): ReactElement {
    const [ membershipCardInfo, setMembershipCardInfo ] = useState<MembershipTypeSaleInfoModel>(null);
    const [ showModal, setShowModal ] = useState<boolean>(false);
    const [ showAlreadyMemberShipCardPopup, setShowAlreadyMemberShipCardPopup ] = useState<boolean>(false);
    const hasMemberShipTypeSaleInfos = membershipTypeSaleInfos && keys(membershipTypeSaleInfos).length > 0;
    const modalContainer: React.RefObject<HTMLDivElement> = useRef();

    const [onContentChange] = useIFrameResize();

    const handleMembershipCardClick = (membershipType: MembershipTypeSaleInfoModel) => {
        setMembershipCardInfo(membershipType);
        setShowModal(true);
    };

    const handleOnAlreadyMemberShipCardClick = (membershipType: MembershipTypeSaleInfoModel) => {
        setMembershipCardInfo(membershipType);
        setShowAlreadyMemberShipCardPopup(true);
    };

    const handleOnRefreshActiveMembershipCards = (membershipTypeId: string, status: number, priceChangeSuggestions: PriceChangeSuggestion[]) => {
        setShowAlreadyMemberShipCardPopup(false);
        refreshActiveMembershipCards(membershipTypeId, status, priceChangeSuggestions);
    };

    const membershipTypeContent = React.useMemo(() => {
        const membershipTypes = [];
        if(hasMemberShipTypeSaleInfos){
            forEach(membershipTypeSaleInfos, (membershipType, i) => {
                const membershipCard: UserMembershipCard = find(activeMembershipCards, ['membershipTypeId', membershipType.id]);

                if(!membershipCard && membershipType.savingAmount > 0)
                {
                    membershipTypes.push(
                        <MembershipCardPub
                            key={i}
                            membershipType={membershipType}
                            onNewMembershipCardClick={handleMembershipCardClick}
                            onAlreadyMembershipCardClick={handleOnAlreadyMemberShipCardClick}
                            showBecomeMemberButtons={showBecomeMemberButtons}  
                            showMoreInfosLink={showMoreInfosLink}  
                        />
                    );
                }
            });
            return membershipTypes;
        }
    }, [activeMembershipCards, hasMemberShipTypeSaleInfos, membershipTypeSaleInfos, showBecomeMemberButtons, showMoreInfosLink]);

    const handleAddMembershipCardToCart = (membershipTypeId: string, status: number, priceChangeSuggestions: PriceChangeSuggestion[]) => {
        setShowModal(false);
        refreshActiveMembershipCards(membershipTypeId, status, priceChangeSuggestions);
    };
    
    if (!hasMemberShipTypeSaleInfos)
        return <></>;

    return <div className="footer">
        <ul className="list-unstyled membership-card-pub-list">
            {membershipTypeContent}
        </ul>

        <div ref={modalContainer}></div>

        {showAlreadyMemberShipCardPopup && <ModalPopup container={modalContainer.current} title={membershipCardInfo.Name} showCloseButton={false} popupWidth={700}>
            <div className="MembershipCardPickupControl">
                <AlreadyMember 
                    membershipTypeIDs={[membershipCardInfo.id]} 
                    addMembershipCardProcessing={false}
                    refreshActiveMembershipCards={handleOnRefreshActiveMembershipCards}
                />
                <div className="actions">
                    <button onClick={() => setShowAlreadyMemberShipCardPopup(false)} className="btn btn-default">{tr("Annuler")}</button>
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        <hr/> 
                    </div>
                </div>
            </div>
        </ModalPopup>}
        {showModal && <ModalPopup container={modalContainer.current} title={membershipCardInfo.Name} showCloseButton={false} popupWidth={700}>
            <div>
                <MembershipCardPickupControl 
                    membershipTypeInfos={[membershipCardInfo]} 
                    refreshActiveMembershipCards={handleAddMembershipCardToCart} 
                    onContentChange={onContentChange}
                    showAlreadyMemberSection={!showBecomeMemberButtons /* If we don`t show it in here, we show it in there */}
                />
            </div>
            <div className="actions">
                <button onClick={() => setShowModal(false)} className="btn btn-default">{tr("Annuler")}</button>
            </div>
        </ModalPopup>}
    </div>;
}
